import { cachedFetch } from "../CachedFetch";

type HashratePoint = {
  timestamp: number;
  avgHashrate: number;
};

type HashrateHistoryResponse = {
  hashrates: HashratePoint[];
};

type WeeklyHashratePoint = {
  timestamp: number;
  avgHashrate: number;
};

const TEN_YEARS = 10;

export default class MempoolSpaceAdapter {
  public static async getLastBlockStats() {
    return await this.getSafely(async () => {
      const response = await cachedFetch("https://mempool.space/api/v1/blocks", { ttlMs: 10 * 60 * 1000 }); // cache for 10 minutes
      // const json = await response.json();
      return response[0];
    });
  }

  public static async getBitcoinMiningStats() {
    return await this.getSafely(async () => {
      const response = await cachedFetch(
        "https://mempool.space/api/v1/mining/hashrate/24h",
      );

      return response;
    });
  }

  public static async getBitcoinMiningHashrate10yWeeklyAverages() {
    return await this.getSafely(async () => {
      const response = await cachedFetch<HashrateHistoryResponse>(
        "https://mempool.space/api/v1/mining/hashrate/10y",
        { ttlMs: 24 * 60 * 60 * 1000 }, // cache one day
      );

      const tenYearsOnly = this.filterByLastYears(response.hashrates ?? [], TEN_YEARS);
      return this.getWeeklyAverages(tenYearsOnly);
    });
  }

  private static filterByLastYears(
    hashrates: HashratePoint[],
    years: number,
  ): HashratePoint[] {
    const cutoffDate = new Date();
    cutoffDate.setUTCFullYear(cutoffDate.getUTCFullYear() - years);
    const cutoffTimestampSeconds = Math.floor(cutoffDate.getTime() / 1000);

    return hashrates.filter((entry) => entry.timestamp >= cutoffTimestampSeconds);
  }

  private static getWeeklyAverages(
    hashrates: HashratePoint[],
  ): WeeklyHashratePoint[] {
    const weekMap = new Map<number, { total: number; count: number }>();

    hashrates.forEach((entry) => {
      const timestampMs = entry.timestamp * 1000;
      const weekStart = this.getUtcWeekStartTimestamp(timestampMs);
      const current = weekMap.get(weekStart);

      if (!current) {
        weekMap.set(weekStart, { total: entry.avgHashrate, count: 1 });
        return;
      }

      current.total += entry.avgHashrate;
      current.count += 1;
    });

    return Array.from(weekMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([weekStartMs, stats]) => ({
        timestamp: Math.floor(weekStartMs / 1000),
        avgHashrate: stats.total / stats.count,
      }));
  }

  private static getUtcWeekStartTimestamp(timestampMs: number): number {
    const date = new Date(timestampMs);
    const dayOfWeek = date.getUTCDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const weekStart = new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate() + mondayOffset,
      ),
    );

    return weekStart.getTime();
  }

  private static async getSafely(callback: () => {}) {
    // TODO: move into separate class
    try {
      const data = await callback();
      return data;
    } catch (e) {
      console.error("Bitnodes error", e);
      return [];
    }
  }
}
